import React, { useState, useContext, useEffect } from 'react';
import '../../../globalstyle/vars.css';
import { ErrorDetails, InitialValuesType, ModalContentType } from '../../types';
import { checkBusinessUser, checkUserValidity, getEmail, getFormData } from '../../services/sitecoreServices';
import { Skeleton } from '../../components/Skeleton';
import { gtagEnvelope } from '../../util/gtagEnvelope';
import { TrialContentContext } from '../../context/ContentContext';
import { Error } from '../Error/Error';
import { TrialView } from './TrialView';
import { FORM_TYPES, MODAL_TYPES, TRIAL_TYPES } from '~/common/consts';
import { postSegmentService } from '~/common/services/trackingServices';
import { getErrorMessage } from '~/common/util/getErrorMessage';
import { getCookie } from '~/common/util/cookies';
import { getEventName, getEventReason } from '~/common/util/getExperimentVersion';
import { getFlowType } from '~/common/util/getFlowType';
import { URISpaceReplace } from '~/common/util/spaceReplace';
import { analyticsEvent } from '~/common/util/analyticsEvent';
import { TrialViewUnified } from './Modals/Unified/TrialViewUnified';
import { formatMarketingAttributionValueFromCookieForAMP } from '~/common/util/formatMarketingAttributionValueFromCookie';
import { TrialViewHero } from './Hero/TrialView';

export const Trial = ({
  modalType,
  handleCallback,
  modalContent,
  isHero,
}: {
  modalType?: string;
  handleCallback?: (step: number) => void;
  modalContent?: ModalContentType;
  isHero?: boolean;
}) => {
  const data = useContext(TrialContentContext);
  const { formType, trialLength, isLoggedIn, trialType } = window.LPFormData;
  const flowType = getFlowType(formType);
  const countryCookie = getCookie('cciso');

  //local state
  const [initialValues, setInitialValues] = useState<InitialValuesType>({});
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const [errorCode, setErrorCode] = useState<string>(null);
  const [trialAllowed, setTrialAllowed] = useState<boolean>(true);
  const [existingUserEmail, setExistingUserEmail] = useState<string>();
  const [loggedInLoading, setLoggedInLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isLoggedIn && data) {
      startLoggedInFlow(data.sku);
    }
  }, [data]);

  useEffect(() => {
    if (isLoggedIn) {
      setLoggedInLoading(true);
    }
    postSegmentService({
      event: `${getEventName(true)}_trial_form_shown`,
      properties: {
        reason: getEventReason(),
        ...(is2Step && modalType !== MODAL_TYPES.UNIFIED && { journey_step: `Step 1` }),
        ...(modalType === MODAL_TYPES.UNIFIED && {
          journey_step: `Step 1 email`,
          sku: 'UNIFIED',
        }),
        ...(trialType === TRIAL_TYPES.PREMIUM && formatMarketingAttributionValueFromCookieForAMP()),
        ...(trialType === TRIAL_TYPES.FAMILIES && { source: `Sitecore` }),
      },
    });
  }, []);

  const startLoggedInFlow = async (sku: string) => {
    try {
      const emailResp = await getEmail();
      const email = emailResp.email;
      if (email && email.trim() !== '' && typeof email === 'string') {
        setExistingUserEmail(email);
        await analyticsEvent(email);
        let accountAllowed;
        if (trialType === TRIAL_TYPES.MSP || trialType === TRIAL_TYPES.FAMILIES) {
          accountAllowed = await checkUserValidity({
            email,
            trialLength: trialLength || 30,
            trialSku: sku,
            ...(trialType === TRIAL_TYPES.FAMILIES && { flowType }),
          });
        } else {
          accountAllowed = await checkBusinessUser({
            flowType,
            trialLength: trialLength || 14,
            trialSku: sku,
          });
        }

        if (accountAllowed?.success) {
          setTrialAllowed(true);
          const initialVals = await getFormData();
          if (initialVals) {
            setInitialValues({
              fullName: `${initialVals.firstName} ${initialVals.lastName}`,
              temp_phone__c: initialVals.phoneNumber,
              title: initialVals.jobTitle,
              company: initialVals.companyName,
              employees: initialVals.companySize,
            });
          }
        } else {
          setTrialAllowed(false);
          setErrorMessage(getErrorMessage(data, accountAllowed.code, existingUserEmail) || data.globalError);
          trackError({
            code: accountAllowed.code,
            message: getErrorMessage(data, accountAllowed.code) || data.globalError,
          });
        }
      } else {
        setErrorMessage(data.globalError);
        trackError({
          code: '500',
          message: 'Start logged in trial email error',
        });
      }
      setLoggedInLoading(false);
    } catch (error) {
      setLoggedInLoading(false);
      setErrorMessage(data.globalError);
      trackError({
        code: '500',
        message: `Start logged in trial flow: ${error.message}`,
        runtime: true,
      });
    }
  };

  const trackError = ({ code, message, runtime, step = 1 }: ErrorDetails) => {
    gtagEnvelope('event', 'trial_page_error', {
      error_type: runtime ? 'runtime_error' : 'processing_error',
      error_code: code,
      error_message: message,
    });
    postSegmentService({
      event: 'trial_submit_processing_error',
      properties: {
        reason: getEventReason(),
        apiErrorDescription: message,
        apiErrorCode: code,
        sku: data.sku,
        ...(is2Step && step && { journey_step: `Step ${step}` }),
      },
    });
    setErrorCode(code);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let emailParam = params.get('email');
    if (typeof emailParam === 'string' && emailParam.indexOf(' ') > -1) {
      emailParam = URISpaceReplace(emailParam);
    }

    if (emailParam) {
      setInitialValues({
        email: emailParam,
        country: countryCookie ? countryCookie : 'US',
      });
    } else {
      setInitialValues({
        country: countryCookie ? countryCookie : 'US',
      });
    }
  }, []);

  const is2Step =
    !!modalType || (trialType === TRIAL_TYPES.BUSINESS && flowType !== FORM_TYPES.PURCHASE && !existingUserEmail);

  if (data === null || loggedInLoading)
    return (
      <Skeleton
        hero={isHero}
        mini={
          is2Step ||
          !!modalType ||
          trialType === TRIAL_TYPES.TEAMS ||
          trialType === TRIAL_TYPES.PREMIUM ||
          trialType === TRIAL_TYPES.FAMILIES
        }
      />
    );

  if (!data || typeof data !== 'object')
    return (
      <Error>
        <h2>Oops!</h2>
        <p>Something went wrong. Please reload or try again later.</p>
      </Error>
    );

  return modalType === MODAL_TYPES.UNIFIED ? (
    <TrialViewUnified
      initialValues={initialValues}
      formError={errorMessage}
      formErrorCode={errorCode}
      setErrorMessage={setErrorMessage}
      directbuy={flowType === FORM_TYPES.PURCHASE}
      data={data}
      trialAllowed={trialAllowed}
      existingUserEmail={existingUserEmail}
      trackError={trackError}
      setErrorCode={setErrorCode}
      is2Step={is2Step}
    />
  ) : isHero ? (
    <TrialViewHero
      initialValues={initialValues}
      formError={errorMessage}
      formErrorCode={errorCode}
      setErrorMessage={setErrorMessage}
      data={data}
      trackError={trackError}
      setErrorCode={setErrorCode}
    />
  ) : (
    <TrialView
      initialValues={initialValues}
      formError={errorMessage}
      formErrorCode={errorCode}
      setErrorMessage={setErrorMessage}
      directbuy={flowType === FORM_TYPES.PURCHASE}
      data={data}
      trialAllowed={trialAllowed}
      existingUserEmail={existingUserEmail}
      trackError={trackError}
      setErrorCode={setErrorCode}
      is2Step={is2Step}
      handleCallback={handleCallback}
      modalContent={modalContent}
    />
  );
};
